import { Badge, cn } from '@recipes/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { useSupportUnread } from '@/features/support/hooks';
import { useFeature } from '@/features/shared/hooks';
import { useAuth } from '@/lib/auth/auth-provider';

const SECTIONS = [
  { to: '/admin', key: 'dashboard', end: true },
  { to: '/admin/users', key: 'users' },
  { to: '/admin/jobs', key: 'jobs' },
  { to: '/admin/flags', key: 'flags' },
  { to: '/admin/ai', key: 'ai' },
  { to: '/admin/nutrition', key: 'nutrition' },
  { to: '/admin/units', key: 'units' },
  { to: '/admin/categories', key: 'categories' },
  { to: '/admin/scraper', key: 'scraper' },
  { to: '/admin/reports', key: 'reports' },
  { to: '/admin/audit', key: 'audit' },
  { to: '/admin/mail', key: 'mail', end: true },
  { to: '/admin/mail/templates', key: 'mailTemplates' },
  { to: '/admin/mail/delivery', key: 'mailDelivery' },
  { to: '/admin/support', key: 'support' },
  { to: '/admin/help', key: 'help' },
] as const;

/** The role SUPPORT sees the support inbox and nothing else of the admin area. */
const SUPPORT_ONLY = new Set<string>(['support']);

/** Wrapper for every admin screen: secondary navigation + page header. */
export function AdminPage({
  section,
  title,
  description,
  actions,
  children,
}: {
  section: (typeof SECTIONS)[number]['key'];
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const inbox = useFeature('support_inbox');
  const unread = useSupportUnread();
  const sections = SECTIONS.filter(
    (s) => (user?.role === 'ADMIN' || SUPPORT_ONLY.has(s.key)) && (s.key !== 'support' || inbox),
  );
  return (
    <PageContainer>
      <nav
        aria-label={t('admin.nav')}
        className="-mx-4 mb-4 overflow-x-auto border-b px-4 md:mx-0 md:px-0"
      >
        <ul className="flex gap-1">
          {sections.map((s) => (
            <li key={s.to} className="shrink-0">
              <NavLink
                to={s.to}
                end={'end' in s ? s.end : false}
                className={({ isActive }) =>
                  cn(
                    'block border-b-2 px-3 py-2 text-sm font-medium',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )
                }
                data-testid={`admin-nav-${s.key}`}
              >
                {t(`admin.sections.${s.key}`)}
                {s.key === 'support' && unread > 0 && (
                  <Badge className="ml-1.5" data-testid="support-unread-badge">
                    {unread > 99 ? '99+' : unread}
                  </Badge>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <FeaturePageHeader
        title={title ?? t(`admin.sections.${section}`)}
        description={description}
        actions={actions}
      />
      {children}
    </PageContainer>
  );
}
